import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { AlertCircle, ChevronDown, Database, LayoutDashboard, Link2, SlidersHorizontal, Trash2 } from 'lucide-react';
import ChampUsagesCompte from '../ChampUsagesCompte';
import BulleSurvol from '../BulleSurvol';
import ModaleDashboardsSurMesure from '../sur-mesure/ModaleDashboardsSurMesure';
import { SOURCES_AVEC_DASHBOARD } from '../ChoixDashboardAnalytics';

/**
 * Les comptes en liste, à la charte des Connecteurs.
 *
 * ## Ce qui change, et pourquoi
 *
 * L'écran groupait les comptes par fournisseur — un titre, puis une grille de cartes. Cela
 * coûtait un intertitre par plateforme pour une information qui tient dans une colonne, et
 * surtout **interdisait tout tri transversal** : impossible de voir d'un coup les comptes sans
 * site, ou de ranger le parc par nom. La plateforme devient donc une colonne, comme la marque
 * dans les Connecteurs.
 *
 * Les classes `kc-*` viennent de `styles/connecteurs.css` et ne sont pas recopiées : une charte
 * qui vit en deux exemplaires diverge au premier ajustement. Seule la largeur des colonnes et ce
 * qui est propre à cet écran vivent dans `styles/comptes.css`.
 *
 * ## Ce que le composant ne fait pas
 *
 * Il ne sait rien du rattachement. Les trois écritures d'un identifiant de compte, les identités
 * d'un site Matomo, l'index des accès refusés : tout cela est résolu par l'écran appelant, qui
 * lui passe des lignes déjà prêtes. Refaire ce calcul ici aurait créé une seconde vérité — et
 * c'est précisément ce genre de divergence qui faisait afficher « Aucun site associé » sur un
 * compte que le Gestionnaire montrait rattaché.
 */

const COLONNES = [
  { cle: 'nom', libelle: 'Compte' },
  { cle: 'plateforme', libelle: 'Plateforme' },
  // La colonne Sites occupe tout l'espace entre Plateforme et État : son contenu se centre,
  // sinon les étiquettes restent collées à gauche et laissent un vide de trois cents pixels.
  { cle: 'sites', libelle: 'Sites', centre: true },
  { cle: 'etat', libelle: 'État' }
];

/*
 * Les comptes qui peuvent porter un dashboard sur mesure : ceux dont l'écran de choix sait
 * afficher les cartes, puisque c'est par là qu'on les ouvre.
 *
 * ⚠️ **Importée, jamais recopiée.** La liste vivait ici en dur, limitée aux trois solutions de
 * mesure, et la restriction était d'interface — « rien n'empêcherait d'héberger un dashboard sur
 * un compte publicitaire, il n'y aurait simplement aucune porte pour l'ouvrir ». La porte existe
 * désormais pour les régies ; recopier la liste garantirait qu'un connecteur ajouté à l'écran de
 * choix n'apparaisse jamais ici, sans que rien ne le signale.
 */

const LIBELLE_ETAT_REFUS = {
  AUTH_EXPIRED: 'Connexion expirée',
  NOT_FOUND: 'Compte introuvable'
};

const ListeComptes = ({
  lignes,
  sites,
  token,
  onRafraichir,
  onSupprimer,
  onAssocier,
  onDissocier,
  /**
   * Ouvre la génération assistée pour un compte, ou `null` si elle n'est pas offerte.
   *
   * ⚠️ C'est l'appelant qui décide — l'éditeur de Kokpit seulement : lire le rôle ici ferait
   * un second endroit où la règle vit, et c'est celui qu'on oublie de changer.
   */
  onGenererDashboard = null
}) => {
  const [tri, setTri] = useState({ cle: 'nom', sens: 'asc' });
  const [menuOuvert, setMenuOuvert] = useState(null);
  const [reglagesOuverts, setReglagesOuverts] = useState({});
  /**
   * Le compte dont on administre les dashboards sur mesure, ou `null`.
   *
   * Tenu ici et non chez l'appelant : la modale n'a besoin que de la ligne et du jeton, que ce
   * composant détient déjà. Remonter l'état ferait traverser deux écrans à une information qui ne
   * concerne qu'un bouton.
   */
  const [dashboardsDuCompte, setDashboardsDuCompte] = useState(null);

  /**
   * Le menu d'association se referme au clic à côté, et à Échap.
   *
   * Un menu flottant qui ne se ferme qu'en recliquant son bouton oblige à retrouver ce bouton —
   * or il est à droite d'une ligne parmi cinquante-sept, et le menu la recouvre.
   *
   * L'écouteur est posé sur `mousedown` et non sur `click` : entre les deux, React a le temps de
   * remonter le menu, et la cible d'un `click` différé n'est alors plus dans le document. Le
   * bouton d'ouverture est exclu, sans quoi son propre clic refermerait ce qu'il vient d'ouvrir.
   *
   * ⚠️ **L'exclusion porte sur `.kbulle`, le conteneur réellement rendu.**
   *
   * Elle visait `.kco-menu`, la boîte que ce menu occupait du temps où il était posé en absolu
   * dans la ligne. Depuis son passage en portail, c'est `BulleSurvol` qui fournit le conteneur
   * et cette classe n'existe plus nulle part : un `mousedown` sur une entrée du menu ne
   * correspondait donc à aucune des deux exclusions, le menu se refermait avant que le `click`
   * n'aboutisse, et **choisir un site ne faisait rien** — sans erreur, sans appel réseau, rien.
   *
   * `.kbulle` sert aussi aux infobulles de survol, mais elles sont en `pointer-events: none` :
   * aucun `mousedown` ne peut les prendre pour cible.
   */
  useEffect(() => {
    if (!menuOuvert) return undefined;

    const auClic = (evenement) => {
      if (evenement.target.closest?.('.kbulle')) return;
      if (evenement.target.closest?.('.kco-ouvrir-menu')) return;
      setMenuOuvert(null);
    };
    const aLaTouche = (evenement) => {
      if (evenement.key === 'Escape') setMenuOuvert(null);
    };

    document.addEventListener('mousedown', auClic);
    document.addEventListener('keydown', aLaTouche);
    return () => {
      document.removeEventListener('mousedown', auClic);
      document.removeEventListener('keydown', aLaTouche);
    };
  }, [menuOuvert]);

  const basculerTri = (cle) => setTri(actuel => (
    actuel.cle === cle
      ? { cle, sens: actuel.sens === 'asc' ? 'desc' : 'asc' }
      : { cle, sens: 'asc' }
  ));

  const triees = useMemo(() => {
    const valeur = (l) => {
      if (tri.cle === 'plateforme') return l.plateforme;
      if (tri.cle === 'sites') return l.sitesAssocies.map(s => s.name).join(' ');
      // L'état se trie par gravité, pas par ordre alphabétique : ce qu'on cherche en
      // cliquant, c'est faire remonter ce qui ne va pas.
      if (tri.cle === 'etat') return l.refus ? '0' : (l.usagesRestreints ? '1' : '2');
      return l.item.name || '';
    };
    const sens = tri.sens === 'asc' ? 1 : -1;
    return [...lignes].sort((a, b) => sens * String(valeur(a)).localeCompare(String(valeur(b)), 'fr'));
  }, [lignes, tri]);

  if (lignes.length === 0) {
    return (
      <div className="kc-liste kco-liste">
        <div className="kc-vide">
          <Database size={40} />
          <p className="kc-vide-titre">Aucun compte</p>
          <p className="kc-vide-texte">
            Ajoutez des comptes publicitaires ou analytiques pour alimenter vos dashboards,
            vos outils et le MCP.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="kc-liste kco-liste">
      <div className="kc-grille kco-grille kc-entete">
        {COLONNES.map(colonne => (
          <button
            key={colonne.cle}
            type="button"
            className={`kc-tri${tri.cle === colonne.cle ? ' actif' : ''}${colonne.centre ? ' kco-tri-centre' : ''}`}
            onClick={() => basculerTri(colonne.cle)}
          >
            {colonne.libelle}
            {tri.cle === colonne.cle
              ? <ChevronDown size={12} style={{ transform: tri.sens === 'desc' ? 'rotate(180deg)' : 'none' }} />
              : <ChevronDown size={12} className="kc-tri-repos" />}
          </button>
        ))}
        <span className="kc-entete-fin">Actions</span>
      </div>

      {triees.map(ligne => {
        const { item, cle, logo, plateforme, qualificatif, sitesAssocies, refus, usagesRestreints, rattachable } = ligne;
        const reglagesOuvert = Boolean(reglagesOuverts[cle]);

        return (
          <div key={cle} className="kc-grille kco-grille kc-ligne kco-ligne">
            <div className="kc-marque">
              <span className="kc-logo">
                {logo ? <img src={logo} alt="" /> : <Database size={17} />}
              </span>
              <span style={{ minWidth: 0 }}>
                <span className="kc-nom" style={{ display: 'block' }}>{item.name}</span>
                {qualificatif && <span className="kc-qualificatif">{qualificatif}</span>}
              </span>
            </div>

            <div className="kc-compte kco-plateforme">{plateforme}</div>

            <div className="kco-sites">
              {sitesAssocies.length > 0
                ? sitesAssocies.map(site => <span key={site.id} className="kco-site">{site.name}</span>)
                : rattachable
                  ? <span className="kco-sans-site">Aucun site</span>
                  : <span className="kco-sans-site">—</span>}
            </div>

            <div className="kco-etats">
              {/* Le message de la régie reste en infobulle : « permission révoquée » et « jeton
                  expiré » n'appellent pas le même geste. */}
              {refus && (
                <span
                  className="kc-etat erreur"
                  title={refus.error
                    ? `${refus.error}${refus.errorCount > 1 ? ` — ${refus.errorCount} échecs consécutifs` : ''}`
                    : 'Accès refusé par la régie'}
                >
                  {LIBELLE_ETAT_REFUS[refus.status] || 'Accès révoqué'}
                </span>
              )}
              {usagesRestreints && (
                <span className="kc-etat inactif" title="Ce compte n'alimente plus toutes ses destinations. Ouvrir le panneau pour voir lesquelles.">
                  Destinations réduites
                </span>
              )}
              {!refus && !usagesRestreints && <span className="kc-etat actif">Actif</span>}
            </div>

            <div className="kc-actions">
              <button
                type="button"
                className="kc-action"
                title="Destinations — où les données de ce compte remontent"
                onClick={() => setReglagesOuverts(p => ({ ...p, [cle]: !p[cle] }))}
              >
                <SlidersHorizontal size={15} />
              </button>
              {/*
                Les dashboards sur mesure, pour toute source que l'écran de choix sait ouvrir.

                Le modèle n'a jamais eu rien de propre à l'analytics — une fiche porte un compte
                quelconque. C'est la porte qui manquait : une régie s'ouvrait droit sur son
                dashboard, sans écran de choix, donc sans endroit où poser une carte. L'écran de
                choix les sert désormais aussi, et c'est lui qui décide de cette liste.
              */}
              {SOURCES_AVEC_DASHBOARD.includes(item.type) && (
                <button
                  type="button"
                  className="kc-action"
                  title="Dashboards sur mesure de ce compte"
                  onClick={() => setDashboardsDuCompte(item)}
                >
                  <LayoutDashboard size={15} />
                </button>
              )}
              {rattachable && sites.length > 0 && (
                <button
                  type="button"
                  className="kc-action kco-ouvrir-menu"
                  title="Associer à un site"
                  onClick={(e) => {
                    if (menuOuvert?.cle === cle) return setMenuOuvert(null);
                    const zone = e.currentTarget.getBoundingClientRect();
                    // Le menu sort par un portail : les lignes n'ont plus de boîte depuis que
                    // la liste est une grille unique, il n'y a donc plus rien à quoi s'ancrer.
                    setMenuOuvert({ cle, x: zone.right - 240, bas: zone.bottom + 6, haut: zone.top - 6 });
                  }}
                >
                  <Link2 size={15} />
                </button>
              )}
              <button
                type="button"
                className="kc-action danger"
                title="Retirer ce compte de la bibliothèque"
                onClick={() => onSupprimer(item.databaseId || item.id)}
              >
                <Trash2 size={15} />
              </button>
            </div>

            {reglagesOuvert && (
              <div className="kco-reglages">
                <ChampUsagesCompte item={item} token={token} onEnregistre={onRafraichir} />
                {refus && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#b91c1c', fontSize: '0.75rem' }}>
                    <AlertCircle size={13} />
                    {refus.errorAt && `Dernier échec le ${new Date(refus.errorAt).toLocaleDateString('fr-FR')}`}
                  </span>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* Le menu d'association, en portail : la liste a `overflow: hidden` et ses lignes n'ont
          plus de boîte depuis qu'elle est une grille unique. */}
      {menuOuvert && ReactDOM.createPortal(
        <BulleSurvol position={menuOuvert}>
          <div className="kco-menu-titre">Associer à un site</div>
          {(() => {
            const ligne = triees.find(l => l.cle === menuOuvert.cle);
            if (!ligne) return null;
            return [...sites]
              .sort((a, b) => {
                const aA = ligne.sitesAssocies.some(s => s.id === a.id);
                const bA = ligne.sitesAssocies.some(s => s.id === b.id);
                if (aA !== bA) return aA ? -1 : 1;
                return a.name.localeCompare(b.name, 'fr');
              })
              .map(site => {
                const dejaAssocie = ligne.sitesAssocies.some(s => s.id === site.id);
                return (
                  <button
                    key={site.id}
                    type="button"
                    className={`kco-menu-item${dejaAssocie ? ' associe' : ''}`}
                    onClick={() => {
                      // Ce qui lie réellement ce compte à ce site, quelle que soit l'écriture
                      // employée à l'époque : n'en retirer qu'une laisserait le rattachement.
                      if (dejaAssocie) onDissocier(ligne, site);
                      else onAssocier(ligne, site);
                      setMenuOuvert(null);
                    }}
                  >
                    {site.name}
                    {dejaAssocie && <span>✓</span>}
                  </button>
                );
              });
          })()}
        </BulleSurvol>,
        document.body
      )}

      {/* Les dashboards sur mesure du compte. La modale sort par un portail — motif du projet
          pour toute modale, afin qu'aucun z-index de parent ne la recouvre. */}
      {dashboardsDuCompte && (
        <ModaleDashboardsSurMesure
          item={dashboardsDuCompte}
          token={token}
          onFermer={() => setDashboardsDuCompte(null)}
          /*
           * ⚠️ La vue de génération est **plein écran**, donc elle ne peut pas vivre dans la
           * modale : la demande remonte jusqu'à `App.js`, et la modale se ferme en chemin. Sans
           * cette fermeture, elle resterait par-dessus l'écran qu'elle vient d'ouvrir — et son
           * voile, en `position: fixed`, le rendrait inutilisable.
           */
          onGenerer={onGenererDashboard
            ? () => {
              const compte = dashboardsDuCompte;
              setDashboardsDuCompte(null);
              onGenererDashboard({
                id: compte.databaseId || compte.id,
                name: compte.name,
                type: compte.type
              });
            }
            : undefined}
        />
      )}
    </div>
  );
};

export default ListeComptes;
